import { createFileRoute } from '@tanstack/react-router';
import * as z from 'zod';
import { CHANGE_REQUEST_STATUS_FILTERS } from '@/entities/change-request';
import { ChangeRequestsPage } from '@/pages/change-requests';
import { requireAuth } from '../require-auth';

/**
 * «Заявки» — the merchant's own moderation queue.
 *
 * PATH-ONLY: deliberately absent from the Eats nav and from every menu. It is
 * a diagnostic view — "where did the product I submitted go?" — reached by URL
 * rather than a tab a merchant would pass through daily.
 *
 * The trailing `_` on `menu_` opts out of nesting: `/eats/menu` renders the
 * catalog and has no `<Outlet />`, so without it this screen would mount inside
 * that page and render blank. Same rule as `profile_.users.tsx`.
 *
 * Both the status pill and the page live in the URL (CLAUDE.md's
 * state-boundary table), so a filtered page is shareable and the back button
 * steps through filter changes.
 */
const changeRequestsSearchSchema = z.object({
  /**
   * The active status pill. Absent is «Все» — a param meaning "no filter" is
   * one the URL should simply not carry.
   *
   * `.catch(undefined)` rather than a hard failure: a hand-edited or stale
   * link should land on the unfiltered queue, not a router error page.
   */
  status: z.enum(CHANGE_REQUEST_STATUS_FILTERS).optional().catch(undefined),
  /** 1-based. Clamped at 1 so a `?page=0` or `?page=-3` cannot send a negative
   * offset the gateway would reject. */
  page: z.number().int().min(1).default(1).catch(1),
});

export const Route = createFileRoute('/eats/menu_/requests')({
  // Auth guard — see `require-auth.ts`. Runs before the component mounts, so
  // no query fires without a session.
  beforeLoad: requireAuth,
  validateSearch: changeRequestsSearchSchema,
  component: ChangeRequestsRoute,
});

function ChangeRequestsRoute() {
  const { status, page } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <ChangeRequestsPage
      status={status}
      page={page}
      // Switching a filter resets to page 1: page 4 of "pending" is almost
      // never a page of "rejected", and landing past the end shows an empty
      // list that reads as "no rejected requests".
      onStatusChange={(next) =>
        void navigate({ search: { status: next, page: 1 }, replace: true })
      }
      onPageChange={(next) =>
        void navigate({ search: (prev) => ({ ...prev, page: next }) })
      }
    />
  );
}
